import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  downloadAccountingSourceDataExport,
  getAccountingTopicWorkspace,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import { isSalonBusinessCpaAccountant } from "../../utils/accountingRoles";
import { getSalonTimezone } from "../../utils/salonTimezone";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";
import {
  accountingContextFromSearchParams,
  accountingPath,
  hasAccountingContext,
} from "./accountingRouteContext";
import { currentMonthValue, money, monthRange } from "./accountingDate";

const CRA_FIELD_LABELS = {
  personal_account_status: "CRA My Account",
  business_account_status: "CRA My Business Account",
  gst_hst_account_status: "GST/HST account",
  payroll_account_status: "Payroll account",
  corporate_tax_account_status: "Corporate tax account",
  personal_authorization_status: "Personal authorization",
  business_authorization_status: "Business authorization",
};

const statusCards = [
  ["records_needed", "Records needed"],
  ["uploaded", "Uploaded"],
  ["in_review", "In review"],
  ["completed", "Done"],
  ["overdue", "Overdue"],
  ["unanswered_questions", "Questions"],
];

function filenameFromDisposition(header, fallback) {
  const match = String(header || "").match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
}

function formatValue(row) {
  if (row?.format === "money") {
    return money(row.value);
  }
  return Number(row?.value || 0).toLocaleString("en-CA");
}

function dateLabel(value) {
  if (!value) return "";
  const source = String(value);
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(source) ? `${source}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function statusClass(value) {
  const normalized = String(value || "").toLowerCase();
  if (["done", "confirmed", "ready", "not_needed"].includes(normalized)) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (["being_reviewed", "requested", "in_progress"].includes(normalized)) {
    return "border-sky-200 bg-sky-50 text-sky-700";
  }
  if (["needed", "needs_new_copy", "not_started", "unknown", "missing"].includes(normalized)) {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  return "border-slate-200 bg-slate-50 text-slate-600";
}

function uploadPath(request, context) {
  return accountingPath("/documents", context, {
    document_type: request.document_type,
    request_id: String(request.id),
  });
}

export default function SalonBusinessCpaTopicWorkspace({ topicKey }) {
  const { token, user, currentSalon } = useAuth();
  const [searchParams] = useSearchParams();
  const accountingContext = useMemo(
    () => accountingContextFromSearchParams(searchParams),
    [searchParams]
  );
  const accountantView = isSalonBusinessCpaAccountant(user);
  const scopedAccountantView = accountantView && hasAccountingContext(accountingContext);
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon || { salon_timezone: user?.salon_timezone }),
    [currentSalon, user?.salon_timezone]
  );
  const [selectedMonth, setSelectedMonth] = useState(() => currentMonthValue(salonTimezone));
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const range = useMemo(() => monthRange(selectedMonth), [selectedMonth]);

  const params = useMemo(
    () => ({
      ...accountingContext,
      start_date: range.start_date,
      end_date: range.end_date,
    }),
    [accountingContext, range.end_date, range.start_date]
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getAccountingTopicWorkspace(token, topicKey, params);
        if (!cancelled) setWorkspace(data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail || "Unable to load this Ellyanna Accounting workspace."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [params, token, topicKey]);

  const handleExport = async () => {
    if (!token || !workspace?.source_export_type) return;
    setExporting(true);
    setError("");
    try {
      const response = await downloadAccountingSourceDataExport(token, {
        ...params,
        export_type: workspace.source_export_type,
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filenameFromDisposition(
        response.headers?.["content-disposition"],
        `${workspace.topic_key || "Ellyanna Accounting"}_review.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to export this review file right now.");
    } finally {
      setExporting(false);
    }
  };

  const requests = Array.isArray(workspace?.requests) ? workspace.requests : [];
  const documents = Array.isArray(workspace?.documents) ? workspace.documents : [];
  const openRequests = requests.filter((request) =>
    ["needed", "needs_new_copy", "being_reviewed"].includes(request.status)
  );

  return (
    <SalonBusinessCpaShell
      title={workspace?.title || "Taxes & reports"}
      description={
        workspace?.description ||
        "Review records needed, uploaded records, source totals, CRA status, and the next step."
      }
      actions={
        <>
          <label className="text-sm font-semibold text-slate-700">
            Month
            <input
              type="month"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
              className="mt-2 h-11 rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
            />
          </label>
          <Link
            to={accountingPath("/monthly-review", accountingContext)}
            className="inline-flex h-11 items-center rounded-full border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Monthly review
          </Link>
        </>
      }
    >
      {error ? (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {!workspace && loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
          Loading workspace...
        </div>
      ) : null}

      {workspace ? (
        <>
          {!workspace.applies_to_profile ? (
            <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-800">
              This workspace is available, but the saved setup does not currently include{" "}
              {workspace.label}. Review business setup if this area should be part of the
              client's Ellyanna Accounting support.
            </div>
          ) : null}

          <section className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
            {statusCards.map(([key, label]) => (
              <div key={key} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  {label}
                </p>
                <p className="mt-2 text-2xl font-semibold text-slate-950">
                  {Number(workspace.status?.[key] || 0).toLocaleString("en-CA")}
                </p>
              </div>
            ))}
          </section>

          <section className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">
                    Next action
                  </p>
                  <h2 className="mt-2 text-xl font-semibold text-slate-950">
                    {workspace.next_step?.label || "No action needed right now"}
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {workspace.next_step?.description ||
                      "Your records are organized for this topic."}
                  </p>
                </div>
                {workspace.next_step?.action_path ? (
                  <Link
                    to={workspace.next_step.action_path}
                    className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-full bg-rose-500 px-5 text-sm font-semibold text-white hover:bg-rose-600"
                  >
                    Open next step
                  </Link>
                ) : null}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-950">
                    Source data
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Ellyanna Booking totals are shown when available for {range.label || "this month"}.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={exporting}
                  className="inline-flex min-h-10 items-center justify-center rounded-full border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
                >
                  {exporting ? "Exporting..." : workspace.source_label || "Export review file"}
                </button>
              </div>
              <dl className="mt-5 grid gap-3 sm:grid-cols-2">
                {(workspace.source_summary || []).map((row) => (
                  <div key={row.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      {row.label}
                    </dt>
                    <dd className="mt-2 text-lg font-semibold text-slate-950">
                      {formatValue(row)}
                    </dd>
                  </div>
                ))}
              </dl>
              <Link
                to={workspace.source_data_path || accountingPath("/data", accountingContext)}
                className="mt-4 inline-flex text-sm font-semibold text-[#2f3566] hover:text-rose-500"
              >
                Open full source data
              </Link>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-950">Records needed</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    These are the open records or answers needed for this workspace.
                  </p>
                </div>
                <Link
                  to={accountingPath("/requests", accountingContext)}
                  className="inline-flex min-h-10 items-center justify-center rounded-full border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  View requests
                </Link>
              </div>
              <div className="mt-5 space-y-3">
                {openRequests.length ? (
                  openRequests.map((request) => (
                    <article key={request.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-950">
                            {request.title}
                          </h3>
                          <p className="mt-2 text-sm leading-6 text-slate-600">
                            {request.client_note || "Upload this record so review can continue."}
                          </p>
                          <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
                            <span className={`rounded-full border px-3 py-1 ${statusClass(request.status)}`}>
                              {request.status_label}
                            </span>
                            {request.due_date ? (
                              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-600">
                                Due {dateLabel(request.due_date)}
                              </span>
                            ) : null}
                            {request.has_unanswered_questions ? (
                              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-700">
                                Answers needed
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <Link
                          to={
                            scopedAccountantView || accountantView
                              ? accountingPath("/requests", accountingContext)
                              : uploadPath(request, accountingContext)
                          }
                          className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full bg-rose-500 px-4 text-sm font-semibold text-white hover:bg-rose-600"
                        >
                          {accountantView ? "Open request" : "Upload"}
                        </Link>
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-700">
                    No open records are showing for this workspace.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-950">Uploaded records</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Recently uploaded files for this topic and their review status.
                  </p>
                </div>
                <Link
                  to={accountingPath("/documents", accountingContext)}
                  className="inline-flex min-h-10 items-center justify-center rounded-full border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Upload documents
                </Link>
              </div>
              <div className="mt-5 space-y-3">
                {documents.length ? (
                  documents.slice(0, 8).map((document) => (
                    <article key={document.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                          <h3 className="truncate text-sm font-semibold text-slate-950">
                            {document.file_name}
                          </h3>
                          <p className="mt-1 text-sm text-slate-500">
                            {document.document_type_label} - {dateLabel(document.created_at_utc)}
                          </p>
                          {document.note ? (
                            <p className="mt-2 text-sm leading-6 text-slate-600">
                              {document.note}
                            </p>
                          ) : null}
                        </div>
                        <span className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${statusClass(document.status)}`}>
                          {document.status_label}
                        </span>
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                    No uploaded records are showing for this workspace yet.
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-950">CRA status</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    CRA account and authorization checks that may affect this topic.
                  </p>
                </div>
                <Link
                  to={accountingPath("/cra-access", accountingContext)}
                  className="inline-flex min-h-10 items-center justify-center rounded-full border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  CRA access
                </Link>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {(workspace.cra_fields || []).length ? (
                  workspace.cra_fields.map((field) => {
                    const value = workspace.cra_status?.[field] || "not_started";
                    const label = workspace.cra_status?.[`${field}_label`] || String(value).replace(/_/g, " ");
                    return (
                      <div key={field} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                          {CRA_FIELD_LABELS[field] || field.replace(/_/g, " ")}
                        </p>
                        <span className={`mt-3 inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusClass(value)}`}>
                          {label}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                    No CRA status item is needed for this workspace right now.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Recent activity</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Recent record updates connected to this workspace.
              </p>
              <div className="mt-5 space-y-3">
                {(workspace.activity || []).length ? (
                  workspace.activity.map((event) => (
                    <article key={event.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-950">
                            {event.title}
                          </h3>
                          {event.body ? (
                            <p className="mt-1 text-sm leading-6 text-slate-600">
                              {event.body}
                            </p>
                          ) : null}
                        </div>
                        <span className="shrink-0 text-xs font-semibold text-slate-500">
                          {dateLabel(event.occurred_at_utc)}
                        </span>
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                    No recent activity is showing for this workspace yet.
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      ) : null}
    </SalonBusinessCpaShell>
  );
}
